import { useState, useEffect } from 'react'
import { LanguageLevel } from '../types/database'
import {
  VoiceSettings,
  LevelSettings,
  getVoiceSettings,
  setVoiceSettings as saveVoiceSettings,
  getMaxCardsInRotation,
  setMaxCardsInRotation as saveMaxCardsInRotation,
  getLevelSettings,
  setLevelSettings as saveLevelSettings,
  getShowSyncStatus,
  setShowSyncStatus as saveShowSyncStatus,
} from '../lib/storage'
import { useAuth } from '../contexts/AuthContext'
import { syncManager } from '../lib/syncManager'
import { db } from '../lib/localDb'

const SUPPORTED_LANGUAGES = [
  { code: 'es', name: 'Spanish' },
]

const ALL_LEVELS: LanguageLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2']

export function Settings() {
  const { user } = useAuth()
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>({})
  const [maxCardsInRotation, setMaxCardsInRotation] = useState(30)
  const [levelSettings, setLevelSettings] = useState<LevelSettings>({
    enabledLevels: ALL_LEVELS,
    filterMode: 'only'
  })
  const [showSyncStatus, setShowSyncStatus] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const testText: { [lang: string]: string } = {
    es: 'Hola, esto es una prueba.',
  }

  const handleForceSync = async () => {
    if (!user) return

    setIsSyncing(true)
    setSyncMessage(null)

    try {
      // Clear local data and re-sync
      await db.cards.clear()
      await db.sources.clear()
      await db.syncMetadata.clear()

      // Trigger fresh sync
      await syncManager.initialSync(user.id)

      setSyncMessage('Sync complete! Refresh the page to see updated cards.')
    } catch (error: any) {
      setSyncMessage(`Sync failed: ${error.message}`)
    } finally {
      setIsSyncing(false)
    }
  }

  useEffect(() => {
    // Load available voices
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices()
      setVoices(availableVoices)
      
      // Log all voices to console for debugging
      console.log('Available voices:', availableVoices.map(v => ({
        name: v.name,
        lang: v.lang,
        voiceURI: v.voiceURI,
        localService: v.localService,
        default: v.default
      })))
    }

    // Load saved settings from storage module
    setVoiceSettings(getVoiceSettings())
    setMaxCardsInRotation(getMaxCardsInRotation())
    setLevelSettings(getLevelSettings())
    setShowSyncStatus(getShowSyncStatus())

    // Some browsers need this event
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices)
    loadVoices()

    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices)
    }
  }, [])

  const getVoicesForLanguage = (langCode: string) => {
    return voices.filter(voice => 
      voice.lang.toLowerCase().startsWith(langCode.toLowerCase())
    ).sort((a, b) => {
      // Prioritize enhanced/premium voices
      const aName = a.name.toLowerCase()
      const bName = b.name.toLowerCase()
      
      // Check for quality indicators
      const aQuality = aName.includes('enhanced') || aName.includes('premium') || 
                      aName.includes('neural') || aName.includes('wavenet') ? 1 : 0
      const bQuality = bName.includes('enhanced') || bName.includes('premium') || 
                      bName.includes('neural') || bName.includes('wavenet') ? 1 : 0
      
      if (aQuality !== bQuality) return bQuality - aQuality
      
      // Then sort alphabetically
      return a.name.localeCompare(b.name)
    })
  }
  
  const getVoiceDisplayName = (voice: SpeechSynthesisVoice, allVoices: SpeechSynthesisVoice[], voiceIndex: number) => {
    // Show more details about the voice
    let name = voice.name
    
    // Find all instances of this voice in the complete list
    const sameVoices = allVoices.filter(v => 
      v.name === voice.name && 
      v.lang === voice.lang &&
      v.voiceURI === voice.voiceURI
    )
    
    if (sameVoices.length > 1) {
      // Multiple identical entries - differentiate by position in the array
      // Get the position of this specific instance
      let instanceNumber = 0
      for (let i = 0; i <= voiceIndex; i++) {
        if (allVoices[i].name === voice.name && 
            allVoices[i].lang === voice.lang &&
            allVoices[i].voiceURI === voice.voiceURI) {
          instanceNumber++
        }
      }
      
      // Based on macOS patterns, later instances are often enhanced versions
      if (sameVoices.length === 2) {
        if (instanceNumber === 1) {
          name += ' (Compact)'
        } else if (instanceNumber === 2) {
          name += ' (Enhanced)'
        }
      } else {
        name += ` (Version ${instanceNumber})`
      }
    } else if (voice.localService) {
      // For single entries, add quality indicators for known premium voices
      const qualityIndicators = ['Samantha', 'Alex', 'Paulina', 'Monica', 'Jorge', 
                                'Amelie', 'Thomas', 'Anna', 'Alice', 'Luciana']
      if (qualityIndicators.some(q => name.includes(q))) {
        name += ' (Premium)'
      }
    }
    
    return `${name} (${voice.lang})${voice.localService ? ' - Local' : ' - Network'}`
  }

  const handleVoiceChange = (language: string, value: string) => {
    // Value format: "voiceURI|index" to handle duplicates
    const newSettings = { ...voiceSettings, [language]: value }
    setVoiceSettings(newSettings)
    saveVoiceSettings(newSettings)
  }

  const testVoice = (language: string) => {
    const selectedValue = voiceSettings[language]
    let voice = null
    
    if (selectedValue) {
      if (selectedValue.includes('|')) {
        // New format with index
        const [, indexStr] = selectedValue.split('|')
        const index = parseInt(indexStr, 10)
        voice = voices[index]
      } else {
        // Old format, try to find by URI
        voice = voices.find(v => v.voiceURI === selectedValue)
      }
    }
    
    const text = testText[language]

    const utterance = new SpeechSynthesisUtterance(text)
    if (voice) {
      utterance.voice = voice
    }
    utterance.rate = 0.9
    
    window.speechSynthesis.cancel()
    window.speechSynthesis.speak(utterance)
  }
  
  const handleMaxCardsChange = (value: number) => {
    setMaxCardsInRotation(value)
    saveMaxCardsInRotation(value)
  }
  
  const handleLevelToggle = (level: LanguageLevel) => {
    const newLevels = levelSettings.enabledLevels.includes(level)
      ? levelSettings.enabledLevels.filter(l => l !== level)
      : [...levelSettings.enabledLevels, level]

    const newSettings = { ...levelSettings, enabledLevels: newLevels }
    setLevelSettings(newSettings)
    saveLevelSettings(newSettings)
  }

  const handleFilterModeChange = (mode: 'only' | 'prefer') => {
    const newSettings = { ...levelSettings, filterMode: mode }
    setLevelSettings(newSettings)
    saveLevelSettings(newSettings)
  }

  const handleShowSyncStatusChange = (show: boolean) => {
    setShowSyncStatus(show)
    saveShowSyncStatus(show)
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Settings</h2>
          
          {/* Study Settings */}
          <div className="mb-8">
            <h3 className="text-base font-medium text-gray-900 mb-4">Study Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="max-cards" className="block text-sm font-medium text-gray-700 mb-2">
                  Max Cards in Rotation
                </label>
                <div className="flex items-center gap-4">
                  <input
                    id="max-cards"
                    type="number"
                    min="1"
                    max="50"
                    value={maxCardsInRotation}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10)
                      if (!isNaN(value) && value >= 1 && value <= 50) {
                        handleMaxCardsChange(value)
                      }
                    }}
                    className="w-20 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                  <span className="text-sm text-gray-500">
                    cards (default: 30)
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Number of cards to keep in rotation during study sessions. Cards closest to mastery are prioritized.
                </p>
              </div>
            </div>
          </div>
          
          {/* Language Level Settings */}
          <div className="mb-8">
            <h3 className="text-base font-medium text-gray-900 mb-4">Language Level Settings</h3>
            
            <div className="space-y-4">
              {/* Level Checkboxes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Active Levels
                </label>
                <div className="flex flex-wrap gap-4">
                  {ALL_LEVELS.map(level => (
                    <label key={level} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={levelSettings.enabledLevels.includes(level)}
                        onChange={() => handleLevelToggle(level)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">{level}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Filter Mode */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter Mode
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="filterMode"
                      value="only"
                      checked={levelSettings.filterMode === 'only'}
                      onChange={() => handleFilterModeChange('only')}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Only see cards for my levels
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="filterMode"
                      value="prefer"
                      checked={levelSettings.filterMode === 'prefer'}
                      onChange={() => handleFilterModeChange('prefer')}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      Prefer cards for my levels
                    </span>
                  </label>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  "Only" mode: Shows only cards matching your selected levels, prioritizing higher levels.<br/>
                  "Prefer" mode: Shows selected levels first (highest to lowest), then other cards.
                </p>
              </div>
            </div>
          </div>
          
          {/* Display Settings */}
          <div className="mb-8">
            <h3 className="text-base font-medium text-gray-900 mb-4">Display Settings</h3>

            <div className="space-y-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showSyncStatus}
                  onChange={(e) => handleShowSyncStatusChange(e.target.checked)}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Show sync status indicator
                </span>
              </label>
              <p className="text-xs text-gray-500 ml-6">
                Display the online/offline status and sync information at the bottom of the screen.
              </p>
            </div>
          </div>

          {/* Data Sync */}
          <div className="mb-8">
            <h3 className="text-base font-medium text-gray-900 mb-4">Data Sync</h3>

            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                If cards are missing audio or other data, force a fresh sync from the server.
              </p>
              <button
                onClick={handleForceSync}
                disabled={isSyncing}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                  isSyncing
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 hover:bg-indigo-700'
                }`}
              >
                {isSyncing ? 'Syncing...' : 'Force Sync'}
              </button>
              {syncMessage && (
                <p className={`text-sm ${syncMessage.includes('failed') ? 'text-red-600' : 'text-green-600'}`}>
                  {syncMessage}
                </p>
              )}
            </div>
          </div>
          
          {/* Voice Settings */}
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-4">Voice Settings</h3>
            
            <div className="space-y-6">
            {SUPPORTED_LANGUAGES.map(lang => {
              const langVoices = getVoicesForLanguage(lang.code)
              
              return (
                <div key={lang.code} className="border-b pb-6 last:border-b-0">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">
                    {lang.name}
                  </h3>
                  
                  <div className="flex items-center gap-4">
                    <select
                      value={voiceSettings[lang.code] || ''}
                      onChange={(e) => handleVoiceChange(lang.code, e.target.value)}
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    >
                      <option value="">Default voice</option>
                      {langVoices.map((voice) => {
                        const globalIndex = voices.indexOf(voice)
                        return (
                          <option key={`${voice.voiceURI}-${globalIndex}`} value={`${voice.voiceURI}|${globalIndex}`}>
                            {getVoiceDisplayName(voice, voices, globalIndex)}
                          </option>
                        )
                      })}
                    </select>
                    
                    <button
                      onClick={() => testVoice(lang.code)}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      🔊 Test
                    </button>
                  </div>
                  
                  {langVoices.length === 0 && (
                    <p className="mt-2 text-sm text-gray-500">
                      No voices available for {lang.name}
                    </p>
                  )}
                </div>
              )
            })}
            </div>
          </div>
          
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Tips</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• On macOS, look for voices marked as "Local" - these are usually higher quality</li>
              <li>• Voices like Samantha, Alex, Paulina, Monica are premium macOS voices</li>
              <li>• To get enhanced voices, download them in System Settings → Accessibility → Live Speech</li>
              <li>• Some browsers may not expose all system voices</li>
            </ul>
          </div>
          
          <details className="mt-4">
            <summary className="cursor-pointer text-sm font-medium text-gray-700">
              Debug: View all available voices ({voices.length} total)
            </summary>
            <div className="mt-2 text-xs font-mono bg-gray-100 p-2 rounded overflow-auto max-h-48">
              {voices.map((voice, i) => (
                <div key={i} className="py-1">
                  {voice.name} | {voice.lang} | {voice.localService ? 'Local' : 'Network'} | {voice.voiceURI}
                </div>
              ))}
            </div>
          </details>
        </div>
      </div>
    </div>
  )
}